import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useTransition, animated } from 'react-spring'
import Label from '../../../atoms/Label/Label'
import SvgChevronLeft from '../../../atoms/Icons/Arrows/ChevronLeft'
import SvgChevronRight from '../../../atoms/Icons/Arrows/ChevronRight'
import styles from './Filters.module.scss'

type TFilter = {
  key: string
  label: string
  count: number
  onClick: () => void
  hideCount?: boolean
}

interface IFiltersProps {
  filters: TFilter[]
  activeFilter: number
  setActiveFilter: (index: number, callback: () => void) => void
}

/** How far a nudge of the arrow buttons moves the strip. Roughly two tabs' worth. */
const SCROLL_STEP = 240

const Filters = ({ filters, activeFilter, setActiveFilter }: IFiltersProps): React.ReactElement => {
  const listRef = useRef<HTMLUListElement | null>(null)
  const [overflow, setOverflow] = useState({ start: false, end: false })

  const measure = useCallback(() => {
    const list = listRef.current
    if (!list) return

    // The fractional pixel of slack keeps the arrow from flickering on at a rounded-off
    // scroll position when the strip is in fact fully scrolled.
    setOverflow({
      start: list.scrollLeft > 1,
      end: list.scrollLeft + list.clientWidth < list.scrollWidth - 1,
    })
  }, [])

  useEffect(() => {
    const list = listRef.current
    if (!list) return

    measure()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)
    observer.observe(list)
    return () => observer.disconnect()
  }, [measure, filters.length])

  // Keeps the selected tab on screen. It can start out scrolled off, since the page can be
  // opened straight onto a filter near the end of the strip.
  useEffect(() => {
    const list = listRef.current
    const item = list?.children[activeFilter] as HTMLElement | undefined
    if (!list || !item) return

    if (item.offsetLeft < list.scrollLeft) {
      list.scrollLeft = item.offsetLeft
    } else if (item.offsetLeft + item.offsetWidth > list.scrollLeft + list.clientWidth) {
      list.scrollLeft = item.offsetLeft + item.offsetWidth - list.clientWidth
    }
  }, [activeFilter, filters.length])

  const nudge = (direction: 1 | -1): void => {
    listRef.current?.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' })
  }

  const transitions = useTransition(filters, filter => filter.key, {
    config: { mass: 1, tension: 300, friction: 30 },
    from: { transform: 'translate3d(0,-40px,0)' },
    enter: { transform: 'translate3d(0,0px,0)' },
    leave: { transform: 'translate3d(0,-40px,0)' },
  })

  return (
    <div className={styles.root}>
      <ul className={styles.filters} ref={listRef} onScroll={measure}>
        {transitions.map(({ item, props, key }, i) => {
          const classes = classNames(styles['filter-item'], {
            [styles.active]: activeFilter === i,
          })

          return (
            <li className={classes} key={key}>
              <animated.div key={key} style={props}>
                <button onClick={setActiveFilter.bind(null, i, item.onClick)}>
                  {!item.hideCount && (
                    <Label circular size="sm" active={activeFilter === i}>
                      {item.count}
                    </Label>
                  )}
                  {item.label}
                </button>
              </animated.div>
            </li>
          )
        })}
      </ul>

      {/* Only rendered when there is something off screen in that direction, so the arrows
          double as the sign that the strip holds more tabs than it is showing. */}
      {overflow.start && (
        <button
          type="button"
          aria-label="Show earlier tabs"
          className={classNames(styles.nudge, styles['nudge-start'])}
          onClick={() => nudge(-1)}
        >
          <SvgChevronLeft />
        </button>
      )}
      {overflow.end && (
        <button
          type="button"
          aria-label="Show later tabs"
          className={classNames(styles.nudge, styles['nudge-end'])}
          onClick={() => nudge(1)}
        >
          <SvgChevronRight />
        </button>
      )}
    </div>
  )
}

export default Filters
